import SwivuModal from "../../../react/components/editors/SwivuModal.jsx";
import { useCallback } from "react";
import EditorBase from "../base/EditorBase.jsx";
import { PERSISTENCE_STATE_KEYS } from "../../../other/constants/PersistenceStateKeys.jsx";

class SwivuAwardTagEditor extends EditorBase {
  static get $name() {
    return "SwivuAwardTagEditor";
  }

  static get type() {
    return "swivuawardtageditor";
  }

  static get configurable() {
    return {
      closable: true,
      floating: true,
      centered: true,
      draggable: true,
      modal: true,
      minWidth: "30em",
      minHeight: "auto",
      width: "35em",
      height: "auto",
      title: "Add a Points Tag",
      contentElementCls: "p-[1.5em] flex-col flex-nowrap gap-[1em]",
      items: {
        description: {
          type: "displayfield",
          cls: "text-sm",
          html: `<p>You're awarding points. Give this batch a short, clear tag so you can find it easily later. Use 1–3 words that describe the timeframe or reason for the award (for example: 'August Points', 'Safety Week', or 'Q4 Bonus').</p>`
        },
        tagInput: {
          type: "combo",
          label: "Points Tag",
          placeholder: `Example: ${new Date().toLocaleString('default', { month: 'long' })} Points`,
          clearable: true,
          editable: true,
          cls: "w-full",
          required: true,
          displayField: "name",
          valueField: "name",
          createOnUnmatched: (input) => ({ name: input })
        },
        hintText: {
          type: "displayfield",
          cls: "text-xs text-gray-500 dark:text-gray-400 mt-[-0.5em]",
          html: "The last tag you used will appear automatically. Rename if needed."
        }
      },
      bbar: {
        items: {
          cancelButton: {
            type: "button",
            text: "Cancel",
            cls: "b-gray",
            onClick: "up.onCancel"
          },
          saveButton: {
            type: "button",
            text: "Use Tag",
            cls: "b-blue",
            onClick: "up.onSave"
          }
        }
      }
    };
  }

  editRecord(record, context, otherParams) {
    super.editRecord(record, context, otherParams);
    this.pointRecords = otherParams.pointRecords || [record];
    this.project = context.project;
    this.user = context.user;
    this.userPreferences = context.userPreferences;

    const { tagInput } = this.widgetMap;

    // Populate combo with existing tags from store
    const existingTags = this.project?.incentivePointTagsStore?.records || [];
    tagInput.store.data = existingTags;

    // Load last used tag from board preferences
    const lastTagIdPref = this.project?.boardPreferencesStore?.find(r => r.key === PERSISTENCE_STATE_KEYS.lastAwardTagId);
    if (lastTagIdPref?.value) {
      const lastTag = this.project.incentivePointTagsStore.getById(lastTagIdPref.value);
      if (lastTag) {
        tagInput.value = lastTag.name;
      }
    }

    tagInput.focus();
  }

  async onSave() {
    const { tagInput } = this.widgetMap;
    const tagName = tagInput.value?.trim();

    if (!tagName) {
      tagInput.setError("Please enter a tag name");
      return;
    }

    const incentivePointTagsStore = this.project.incentivePointTagsStore;

    // Find or create the tag
    let tagRecord = incentivePointTagsStore.find(tag => tag.name === tagName);

    if (!tagRecord) {
      // Create new tag
      tagRecord = incentivePointTagsStore.add({
        name: tagName,
        boardId: this.project.boardStore.first?.id
      })[0];

      // Wait for sync to get the ID from backend
      await this.project.commitAsync();
    }

    // Update board preferences with last used tag
    if (tagRecord.id && this.userPreferences) {
      this.userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.lastAwardTagId, String(tagRecord.id), true);
    }

    // Update all point records with the tag
    this.pointRecords.forEach(pointRecord => {
      pointRecord.tagId = tagRecord.id;
    });

    this.close();
  }

  onCancel() {
    this.close();
  }
}

SwivuAwardTagEditor.initClass();

const SwivuAwardTagEditorPopup = ({ context, record, onClose, isShow, editorInstance }) => {
  const onShowHandler = useCallback((editor, otherParams = {}) => {
    editor.project = context.project;
    editor.editRecord(record, context, Object.assign(otherParams, record));
    editorInstance.editorRef = editor;
  }, [context, record, editorInstance])

  return (
    <SwivuModal
      widgetType={SwivuAwardTagEditor.type}
      isShow={isShow}
      onShow={onShowHandler}
      onHide={onClose}
    />
  )
}

export default SwivuAwardTagEditorPopup;
